import {
  Archive,
  ArrowRight,
  Check,
  ChevronDown,
  Clock3,
  CloudUpload,
  Download,
  Eye,
  File,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Files,
  FolderOpen,
  Grid2X2,
  Globe2,
  HardDrive,
  Home,
  LayoutList,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Menu,
  MoreVertical,
  Plus,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  Trash2,
  Upload,
  UserRound,
  X,
  Zap,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import type { DriveFile, SessionResponse, User } from "./types";

type ApiError = { error?: string };
type ViewMode = "grid" | "list";
type Section = "all" | "recent" | "starred";

export default function App() {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [notice, setNotice] = useState("");
  const [path, setPath] = useState(window.location.pathname);

  function navigate(to: string) {
    window.history.pushState({}, "", to);
    setPath(new URL(to, window.location.origin).pathname);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    const authError = new URLSearchParams(window.location.search).get("authError");
    if (authError) {
      setNotice(authError);
      window.history.replaceState({}, "", window.location.pathname);
    }
    api<SessionResponse>("/api/session")
      .then((result) => setSession(result ?? { user: null, googleConfigured: false }))
      .catch(() => setSession({ user: null, googleConfigured: false }));
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  if (!session) return <LoadingScreen />;
  if (path === "/" || (path !== "/app" && path !== "/login")) {
    return <LandingPage user={session.user} onLogin={() => navigate("/login")} onRegister={() => navigate("/login?mode=register")} onOpenApp={() => navigate(session.user ? "/app" : "/login")} />;
  }
  if (!session.user) {
    return (
      <AuthScreen
        googleConfigured={session.googleConfigured}
        initialNotice={notice}
        initialMode={new URLSearchParams(window.location.search).get("mode") === "register" ? "register" : "login"}
        onBack={() => navigate("/")}
        onAuthenticated={(user) => { setSession({ ...session, user }); navigate("/app"); }}
      />
    );
  }

  return (
    <DriveApp
      user={session.user}
      onLogout={() => { setSession({ ...session, user: null }); navigate("/login"); }}
    />
  );
}

function LandingPage({ user, onLogin, onRegister, onOpenApp }: { user: User | null; onLogin: () => void; onRegister: () => void; onOpenApp: () => void }) {
  return (
    <main className="landing-shell">
      <nav className="landing-nav">
        <Brand />
        <div className="landing-links"><a href="#mogelijkheden">Mogelijkheden</a><a href="#veiligheid">Veiligheid</a></div>
        <div className="landing-nav-actions">
          {user ? <button className="landing-login" onClick={onOpenApp}>Hoi, {firstName(user.name)}</button> : <button className="landing-login" onClick={onLogin}>Inloggen</button>}
          <button className="primary-button" onClick={user ? onOpenApp : onRegister}>{user ? "Open mijn bestanden" : "Gratis beginnen"}<ArrowRight size={16} /></button>
        </div>
      </nav>

      <section className="landing-hero">
        <div className="hero-noise" />
        <div className="hero-copy">
          <span className="landing-eyebrow"><span /> Veilig. Snel. Helemaal van jou.</span>
          <h1>Al je bestanden,<br /><em>één rustige plek.</em></h1>
          <p>Van dierbare foto’s tot belangrijke documenten. FileShare houdt alles overzichtelijk, privé en altijd binnen handbereik.</p>
          <div className="hero-actions">
            <button className="hero-primary" onClick={onOpenApp}>{user ? "Naar mijn bestanden" : "Begin gratis"}<ArrowRight /></button>
            {!user && <button className="hero-secondary" onClick={onLogin}>Ik heb al een account</button>}
          </div>
          <div className="hero-trust"><span><Check size={15} /> Privé per gebruiker</span><span><Check size={15} /> Geen creditcard nodig</span><span><Check size={15} /> Elk bestandstype</span></div>
        </div>

        <div className="hero-product" aria-label="Voorbeeld van de FileShare bestandsomgeving">
          <div className="product-window">
            <div className="window-top"><span className="window-dots"><i /><i /><i /></span><div><Search size={14} /> Zoeken in bestanden</div><span className="mini-avatar">FH</span></div>
            <div className="window-body">
              <aside><span className="mini-logo"><Brand /></span><b><Plus /> Nieuw</b><a className="selected"><Home /> Mijn bestanden</a><a><Clock3 /> Recent</a><a><Star /> Met ster</a></aside>
              <div className="window-content">
                <div className="window-title"><div><small>Goedemiddag</small><strong>Mijn bestanden</strong></div><button><Upload /> Uploaden</button></div>
                <div className="mock-drop"><CloudUpload /><span><strong>Sleep bestanden hierheen</strong><small>of klik om te kiezen</small></span></div>
                <div className="mock-label"><strong>Recent</strong><span><Grid2X2 /><LayoutList /></span></div>
                <div className="mock-files">
                  <div className="mock-file mock-photo"><div><span className="mock-sun" /><span className="mock-mountain" /></div><p><FileImage /> Vakantie.jpg</p></div>
                  <div className="mock-file mock-pdf"><div><FileText /><b>PDF</b></div><p><FileText /> Contract.pdf</p></div>
                  <div className="mock-file mock-video"><div><FileVideo /><span>0:42</span></div><p><FileVideo /> Herinnering.mp4</p></div>
                </div>
              </div>
            </div>
          </div>
          <div className="hero-float float-secure"><ShieldCheck /><span><strong>Privé opgeslagen</strong><small>Alleen zichtbaar voor jou</small></span></div>
          <div className="hero-float float-upload"><span><Check /></span><div><strong>Upload voltooid</strong><small>Vakantie.jpg · 3,8 MB</small></div></div>
        </div>
      </section>

      <section className="landing-proof">
        <span>Gebouwd op</span><strong>Cloudflare</strong><i /> <span>Snelle wereldwijde opslag</span><i /> <span>Server-side beveiliging</span>
      </section>

      <section className="landing-features" id="mogelijkheden">
        <div className="section-heading"><span>Alles wat je nodig hebt</span><h2>Minder zoeken. Meer terugvinden.</h2><p>Een bestandsomgeving die vertrouwd voelt, zonder de rommel.</p></div>
        <div className="feature-grid">
          <article className="feature-large"><div className="feature-icon"><FileImage /></div><h3>Bekijk zonder te downloaden</h3><p>Foto’s, PDF’s, video’s, audio en tekst open je direct vanuit je overzicht.</p><div className="preview-stack"><span className="stack-one"><FileImage /></span><span className="stack-two"><FileText /></span><span className="stack-three"><FileVideo /></span></div></article>
          <article><div className="feature-icon icon-blue"><Search /></div><h3>Vind het meteen</h3><p>Zoek razendsnel op bestandsnaam en wissel tussen een rustig raster of compacte lijst.</p></article>
          <article><div className="feature-icon icon-gold"><Star /></div><h3>Belangrijk bij de hand</h3><p>Geef bestanden een ster en vind ze terug in je persoonlijke selectie.</p></article>
          <article><div className="feature-icon icon-purple"><Files /></div><h3>Elk bestandstype</h3><p>Afbeeldingen, documenten, archieven, apps en meer — FileShare sluit niets uit.</p></article>
          <article><div className="feature-icon icon-coral"><Zap /></div><h3>Slepen en klaar</h3><p>Sleep één of meerdere bestanden naar je scherm. De upload start direct.</p></article>
        </div>
      </section>

      <section className="security-section" id="veiligheid">
        <div className="security-visual"><div className="security-orbit orbit-one" /><div className="security-orbit orbit-two" /><div className="security-core"><ShieldCheck /></div><span className="security-chip chip-one"><LockKeyhole /> HttpOnly-sessies</span><span className="security-chip chip-two"><UserRound /> RLS per eigenaar</span><span className="security-chip chip-three"><Globe2 /> Cloudflare Edge</span></div>
        <div className="security-copy"><span className="landing-eyebrow dark"><span /> Veiligheid eerst</span><h2>Jouw bestanden zijn<br />ook echt van jou.</h2><p>Inloggen en bestandscontrole gebeuren op de server. Elke databasevraag wordt aan jouw gebruikers-id gekoppeld en opslag is nooit rechtstreeks zichtbaar vanuit de browser.</p><ul><li><Check /> Server-side Google- en e-mailinloggen</li><li><Check /> Versleutelde, HttpOnly sessiecookie</li><li><Check /> Eigenaarscontrole bij bekijken, downloaden en verwijderen</li></ul></div>
      </section>

      <section className="landing-cta"><div><span><Sparkles /> Klaar voor een rustiger overzicht?</span><h2>Geef je bestanden een fijne plek.</h2><p>Maak je account en begin direct met veilig opslaan.</p><button onClick={onOpenApp}>{user ? "Open mijn bestanden" : "Maak gratis een account"}<ArrowRight /></button></div></section>

      <footer className="landing-footer"><Brand /><p>Veilige bestandsopslag, gemaakt met Cloudflare.</p><span>© {new Date().getFullYear()} FileShare</span></footer>
    </main>
  );
}

function AuthScreen({
  googleConfigured,
  initialNotice,
  initialMode,
  onBack,
  onAuthenticated,
}: {
  googleConfigured: boolean;
  initialNotice: string;
  initialMode: "login" | "register";
  onBack: () => void;
  onAuthenticated: (user: User) => void;
}) {
  const [mode, setMode] = useState<"login" | "register">(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(initialNotice);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api<{ user: User }>(`/api/${mode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password }),
      });
      onAuthenticated(result.user);
    } catch (reason) {
      setError(messageFrom(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-story">
        <div className="story-glow story-glow-one" />
        <div className="story-glow story-glow-two" />
        <Brand light />
        <div className="story-copy">
          <span className="eyebrow"><Sparkles size={15} /> Eén plek voor alles</span>
          <h1>Jouw bestanden.<br /><em>Altijd dichtbij.</em></h1>
          <p>Bewaar foto’s, documenten, video’s en meer. Veilig, overzichtelijk en vanaf elk apparaat bereikbaar.</p>
          <div className="story-points">
            <span><Check size={16} /> Privé per account</span>
            <span><Check size={16} /> Directe foto- en PDF-preview</span>
            <span><Check size={16} /> Elk bestandstype welkom</span>
          </div>
        </div>
        <div className="floating-files" aria-hidden="true">
          <div className="float-file float-image"><FileImage /></div>
          <div className="float-file float-pdf"><FileText /></div>
          <div className="float-file float-video"><FileVideo /></div>
        </div>
        <p className="story-foot"><ShieldCheck size={16} /> Beveiligd door Cloudflare</p>
      </section>

      <section className="auth-panel">
        <div className="auth-card">
          <button className="auth-back" type="button" onClick={onBack}>← Terug naar home</button>
          <div className="mobile-brand"><Brand /></div>
          <span className="auth-kicker">Welkom bij FileShare</span>
          <h2>{mode === "login" ? "Fijn je weer te zien" : "Maak je account"}</h2>
          <p className="auth-subtitle">
            {mode === "login" ? "Log in om verder te gaan naar je bestanden." : "Begin in een paar seconden met veilig opslaan."}
          </p>

          {error && <div className="alert" role="alert">{error}</div>}

          <button
            className="google-button"
            type="button"
            onClick={() => { window.location.href = "/api/auth/google"; }}
            disabled={!googleConfigured}
            title={googleConfigured ? "Doorgaan met Google" : "Google OAuth moet nog als Cloudflare-secret worden ingesteld"}
          >
            <GoogleMark />
            {googleConfigured ? "Doorgaan met Google" : "Google-inloggen nog niet ingesteld"}
          </button>

          <div className="divider"><span>of met e-mail</span></div>

          <form onSubmit={submit} className="auth-form">
            {mode === "register" && (
              <label>
                <span>Naam</span>
                <div className="input-wrap"><UserRound size={18} /><input autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jouw naam" required minLength={2} maxLength={80} /></div>
              </label>
            )}
            <label>
              <span>E-mailadres</span>
              <div className="input-wrap"><span className="at-icon">@</span><input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="naam@voorbeeld.nl" required /></div>
            </label>
            <label>
              <span>Wachtwoord</span>
              <div className="input-wrap"><LockKeyhole size={18} /><input type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimaal 10 tekens" required minLength={10} /></div>
            </label>
            <button className="primary-button auth-submit" disabled={busy}>
              {busy && <LoaderCircle className="spin" size={18} />}
              {mode === "login" ? "Inloggen" : "Account maken"}
            </button>
          </form>

          <p className="auth-switch">
            {mode === "login" ? "Nog geen account?" : "Heb je al een account?"}{" "}
            <button type="button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>
              {mode === "login" ? "Gratis registreren" : "Log hier in"}
            </button>
          </p>
          <p className="auth-legal">Door verder te gaan ga je akkoord met veilig en verantwoordelijk gebruik van FileShare.</p>
        </div>
      </section>
    </main>
  );
}

function DriveApp({ user, onLogout }: { user: User; onLogout: () => void }) {
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [section, setSection] = useState<Section>("all");
  const [view, setView] = useState<ViewMode>("grid");
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState<DriveFile | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DriveFile | null>(null);
  const [toast, setToast] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);

  useEffect(() => {
    api<{ files: DriveFile[] }>("/api/files")
      .then((result) => setFiles(result.files))
      .catch((reason) => showToast(messageFrom(reason)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(""), 4200);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const visibleFiles = useMemo(() => {
    const search = query.trim().toLocaleLowerCase("nl");
    const recentCutoff = Date.now() - 1000 * 60 * 60 * 24 * 30;
    return files.filter((file) => {
      if (section === "starred" && !file.starred) return false;
      if (section === "recent" && new Date(file.uploadedAt).getTime() < recentCutoff) return false;
      return !search || file.name.toLocaleLowerCase("nl").includes(search);
    });
  }, [files, query, section]);

  const usedBytes = files.reduce((total, file) => total + file.size, 0);

  function showToast(message: string) {
    setToast(message);
  }

  async function upload(selected: FileList | File[]) {
    const selectedFiles = Array.from(selected);
    if (!selectedFiles.length) return;
    if (selectedFiles.length > 50) return showToast("Kies maximaal 50 bestanden per upload.");
    setUploading(true);
    const form = new FormData();
    selectedFiles.forEach((file) => form.append("files", file));
    try {
      const result = await api<{ files: DriveFile[] }>("/api/files", { method: "POST", body: form });
      setFiles((current) => [...result.files, ...current]);
      showToast(`${result.files.length} ${result.files.length === 1 ? "bestand" : "bestanden"} veilig opgeslagen.`);
    } catch (reason) {
      showToast(messageFrom(reason));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function toggleStar(file: DriveFile) {
    const next = !file.starred;
    setFiles((current) => current.map((item) => item.id === file.id ? { ...item, starred: next } : item));
    try {
      await api(`/api/files/${file.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ starred: next }),
      });
    } catch (reason) {
      setFiles((current) => current.map((item) => item.id === file.id ? file : item));
      showToast(messageFrom(reason));
    }
  }

  async function removeFile() {
    if (!deleteTarget) return;
    try {
      await api(`/api/files/${deleteTarget.id}`, { method: "DELETE" });
      setFiles((current) => current.filter((file) => file.id !== deleteTarget.id));
      if (preview?.id === deleteTarget.id) setPreview(null);
      showToast("Bestand verwijderd.");
    } catch (reason) {
      showToast(messageFrom(reason));
    } finally {
      setDeleteTarget(null);
    }
  }

  async function logout() {
    try { await api("/api/logout", { method: "POST" }); } finally { onLogout(); }
  }

  function onDragEnter(event: React.DragEvent) {
    event.preventDefault();
    dragDepth.current += 1;
    if (event.dataTransfer.types.includes("Files")) setDragging(true);
  }

  function onDragLeave(event: React.DragEvent) {
    event.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current === 0) setDragging(false);
  }

  function onDrop(event: React.DragEvent) {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    void upload(event.dataTransfer.files);
  }

  return (
    <div className="drive-shell" onDragEnter={onDragEnter} onDragOver={(e) => e.preventDefault()} onDragLeave={onDragLeave} onDrop={onDrop}>
      <aside className={`sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-top"><Brand /><button className="icon-button mobile-close" onClick={() => setSidebarOpen(false)}><X /></button></div>
        <button className="new-button" onClick={() => fileInput.current?.click()} disabled={uploading}>
          {uploading ? <LoaderCircle className="spin" /> : <Plus />} <span>{uploading ? "Uploaden…" : "Nieuw bestand"}</span>
        </button>
        <input ref={fileInput} type="file" multiple hidden onChange={(e) => e.target.files && void upload(e.target.files)} />

        <nav className="drive-nav" aria-label="Bestandsweergaven">
          <NavButton active={section === "all"} icon={<Home />} label="Mijn bestanden" onClick={() => { setSection("all"); setSidebarOpen(false); }} />
          <NavButton active={section === "recent"} icon={<Clock3 />} label="Recent" onClick={() => { setSection("recent"); setSidebarOpen(false); }} />
          <NavButton active={section === "starred"} icon={<Star />} label="Met ster" onClick={() => { setSection("starred"); setSidebarOpen(false); }} />
        </nav>

        <div className="storage-card">
          <div className="storage-title"><HardDrive size={18} /><span>Opslag</span></div>
          <div className="storage-track"><span style={{ width: `${Math.min(100, Math.max(2, usedBytes / (1024 ** 3) * 100))}%` }} /></div>
          <p><strong>{formatBytes(usedBytes)}</strong> gebruikt</p>
          <small>Cloudflare R2-opslag</small>
        </div>
        <div className="sidebar-user">
          <Avatar user={user} />
          <div><strong>{user.name}</strong><span>{user.email}</span></div>
          <button className="icon-button" onClick={logout} title="Uitloggen"><LogOut size={18} /></button>
        </div>
      </aside>
      {sidebarOpen && <button className="sidebar-scrim" aria-label="Menu sluiten" onClick={() => setSidebarOpen(false)} />}

      <main className="drive-main">
        <header className="drive-header">
          <button className="icon-button menu-button" onClick={() => setSidebarOpen(true)}><Menu /></button>
          <div className="search-box"><Search size={20} /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Zoeken in bestanden" aria-label="Zoeken in bestanden" />{query && <button onClick={() => setQuery("")}><X size={17} /></button>}</div>
          <div className="header-actions">
            <span className="secure-pill"><ShieldCheck size={16} /> Privé</span>
            <Avatar user={user} compact />
          </div>
        </header>

        <div className="content-wrap">
          <section className="welcome-row">
            <div>
              <span className="page-kicker">{greeting()}, {firstName(user.name)}</span>
              <h1>{sectionTitle(section)}</h1>
              <p>{sectionSubtitle(section, visibleFiles.length)}</p>
            </div>
            <button className="primary-button upload-top" onClick={() => fileInput.current?.click()} disabled={uploading}>
              {uploading ? <LoaderCircle className="spin" size={18} /> : <Upload size={18} />}
              {uploading ? "Uploaden…" : "Uploaden"}
            </button>
          </section>

          <section className="quick-drop" onClick={() => fileInput.current?.click()}>
            <div className="drop-icon"><CloudUpload /></div>
            <div><strong>Sleep bestanden hierheen</strong><span>of klik om te kiezen — alle bestandstypen zijn toegestaan</span></div>
            <button className="choose-button">Bestanden kiezen</button>
          </section>

          <section className="files-section">
            <div className="files-toolbar">
              <div><h2>{query ? `Resultaten voor “${query}”` : sectionTitle(section)}</h2><span>{visibleFiles.length} {visibleFiles.length === 1 ? "bestand" : "bestanden"}</span></div>
              <div className="view-toggle" aria-label="Weergave kiezen">
                <button className={view === "grid" ? "active" : ""} onClick={() => setView("grid")} title="Raster"><Grid2X2 /></button>
                <button className={view === "list" ? "active" : ""} onClick={() => setView("list")} title="Lijst"><LayoutList /></button>
              </div>
            </div>

            {loading ? <FileSkeletons /> : visibleFiles.length === 0 ? (
              <EmptyState query={query} section={section} onUpload={() => fileInput.current?.click()} />
            ) : view === "grid" ? (
              <div className="file-grid">
                {visibleFiles.map((file) => <FileCard key={file.id} file={file} onPreview={() => setPreview(file)} onStar={() => void toggleStar(file)} onDelete={() => setDeleteTarget(file)} />)}
              </div>
            ) : (
              <div className="file-list">
                <div className="list-head"><span>Naam</span><span>Type</span><span>Gewijzigd</span><span>Grootte</span><span /></div>
                {visibleFiles.map((file) => <FileListRow key={file.id} file={file} onPreview={() => setPreview(file)} onStar={() => void toggleStar(file)} onDelete={() => setDeleteTarget(file)} />)}
              </div>
            )}
          </section>
        </div>
      </main>

      {dragging && <div className="drop-overlay"><div><CloudUpload /><h2>Laat los om te uploaden</h2><p>Je bestanden worden privé aan jouw account gekoppeld.</p></div></div>}
      {preview && <PreviewModal file={preview} onClose={() => setPreview(null)} onDelete={() => setDeleteTarget(preview)} onStar={() => void toggleStar(preview)} />}
      {deleteTarget && <ConfirmDelete file={deleteTarget} onCancel={() => setDeleteTarget(null)} onConfirm={() => void removeFile()} />}
      {toast && <div className="toast"><Check size={17} />{toast}</div>}
    </div>
  );
}

function FileCard({ file, onPreview, onStar, onDelete }: FileActionsProps) {
  return (
    <article className="file-card">
      <button className="preview-button" onClick={onPreview} aria-label={`${file.name} bekijken`}><FilePreview file={file} /></button>
      <div className="file-card-info">
        <div className={`small-file-icon ${fileColor(file)}`}>{fileIcon(file, 18)}</div>
        <div className="file-name-block"><strong title={file.name}>{file.name}</strong><span>{formatDate(file.uploadedAt)} · {formatBytes(file.size)}</span></div>
        <FileMenu file={file} onPreview={onPreview} onStar={onStar} onDelete={onDelete} />
      </div>
    </article>
  );
}

function FileListRow({ file, onPreview, onStar, onDelete }: FileActionsProps) {
  return (
    <div className="file-row" onDoubleClick={onPreview}>
      <button className="row-name" onClick={onPreview}><span className={`small-file-icon ${fileColor(file)}`}>{fileIcon(file, 18)}</span><strong title={file.name}>{file.name}</strong></button>
      <span>{friendlyType(file)}</span><span>{formatDate(file.uploadedAt)}</span><span>{formatBytes(file.size)}</span>
      <FileMenu file={file} onPreview={onPreview} onStar={onStar} onDelete={onDelete} />
    </div>
  );
}

function FileMenu({ file, onPreview, onStar, onDelete }: FileActionsProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="file-menu">
      <button className="icon-button" onClick={() => setOpen(!open)} aria-label="Bestandsacties"><MoreVertical size={19} /></button>
      {open && <>
        <button className="menu-closer" aria-label="Menu sluiten" onClick={() => setOpen(false)} />
        <div className="menu-popover">
          <button onClick={() => { onPreview(); setOpen(false); }}><Eye /> Bekijken</button>
          <a href={contentUrl(file, true)} onClick={() => setOpen(false)}><Download /> Downloaden</a>
          <button onClick={() => { onStar(); setOpen(false); }}><Star className={file.starred ? "starred" : ""} /> {file.starred ? "Ster verwijderen" : "Ster toevoegen"}</button>
          <hr /><button className="danger" onClick={() => { onDelete(); setOpen(false); }}><Trash2 /> Verwijderen</button>
        </div>
      </>}
    </div>
  );
}

function FilePreview({ file }: { file: DriveFile }) {
  const url = contentUrl(file);
  if (file.type.startsWith("image/")) return <img src={url} alt="" loading="lazy" />;
  if (file.type === "application/pdf") return <iframe src={`${url}#page=1&toolbar=0&navpanes=0&scrollbar=0`} title={`Voorbeeld van ${file.name}`} tabIndex={-1} />;
  return <div className={`generic-preview ${fileColor(file)}`}>{fileIcon(file, 48)}<span>{extension(file.name)}</span></div>;
}

function PreviewModal({ file, onClose, onDelete, onStar }: { file: DriveFile; onClose: () => void; onDelete: () => void; onStar: () => void }) {
  const url = contentUrl(file);
  return (
    <div className="modal-backdrop preview-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="preview-modal">
        <header><div className={`small-file-icon ${fileColor(file)}`}>{fileIcon(file, 18)}</div><div><strong>{file.name}</strong><span>{friendlyType(file)} · {formatBytes(file.size)}</span></div><div className="preview-actions"><button className={`icon-button ${file.starred ? "starred" : ""}`} onClick={onStar} title="Ster"><Star /></button><a className="icon-button" href={contentUrl(file, true)} title="Downloaden"><Download /></a><button className="icon-button danger-icon" onClick={onDelete} title="Verwijderen"><Trash2 /></button><button className="icon-button" onClick={onClose} title="Sluiten"><X /></button></div></header>
        <div className="preview-stage">
          {file.type.startsWith("image/") ? <img src={url} alt={file.name} />
            : file.type === "application/pdf" ? <iframe src={`${url}#view=FitH`} title={file.name} />
              : file.type.startsWith("video/") ? <video controls autoPlay src={url} />
                : file.type.startsWith("audio/") ? <div className="audio-preview"><div className={`large-file-icon ${fileColor(file)}`}>{fileIcon(file, 60)}</div><h3>{file.name}</h3><audio controls autoPlay src={url} /></div>
                  : file.type.startsWith("text/") || file.type === "application/json" ? <iframe className="text-preview" src={url} title={file.name} />
                    : <div className="no-preview"><div className={`large-file-icon ${fileColor(file)}`}>{fileIcon(file, 64)}</div><h3>Geen voorbeeld beschikbaar</h3><p>Dit bestandstype kun je wel veilig downloaden.</p><a className="primary-button" href={contentUrl(file, true)}><Download /> Download bestand</a></div>}
        </div>
      </div>
    </div>
  );
}

function ConfirmDelete({ file, onCancel, onConfirm }: { file: DriveFile; onCancel: () => void; onConfirm: () => void }) {
  return <div className="modal-backdrop"><div className="confirm-modal"><div className="confirm-icon"><Trash2 /></div><h3>Bestand verwijderen?</h3><p>“{file.name}” wordt definitief uit jouw opslag verwijderd. Dit kan niet ongedaan worden gemaakt.</p><div><button className="secondary-button" onClick={onCancel}>Annuleren</button><button className="danger-button" onClick={onConfirm}>Verwijderen</button></div></div></div>;
}

function EmptyState({ query, section, onUpload }: { query: string; section: Section; onUpload: () => void }) {
  return <div className="empty-state"><div className="empty-illustration"><FolderOpen /><span /></div><h3>{query ? "Geen bestanden gevonden" : section === "starred" ? "Nog geen bestanden met ster" : "Hier is het nog rustig"}</h3><p>{query ? "Probeer een andere zoekterm." : section === "starred" ? "Geef belangrijke bestanden een ster om ze hier snel terug te vinden." : "Upload je eerste bestand en het verschijnt meteen in je overzicht."}</p>{!query && section !== "starred" && <button className="primary-button" onClick={onUpload}><Plus /> Eerste bestand uploaden</button>}</div>;
}

function FileSkeletons() {
  return <div className="file-grid">{Array.from({ length: 8 }, (_, index) => <div className="file-card skeleton" key={index}><div /><span /><small /></div>)}</div>;
}

function NavButton({ active, icon, label, onClick }: { active: boolean; icon: React.ReactElement; label: string; onClick: () => void }) {
  return <button className={active ? "active" : ""} onClick={onClick}>{icon}<span>{label}</span></button>;
}

function Brand({ light = false }: { light?: boolean }) {
  return <div className={`brand ${light ? "brand-light" : ""}`}><span className="brand-mark"><span /><span /><span /></span><strong>FileShare</strong></div>;
}

function Avatar({ user, compact = false }: { user: User; compact?: boolean }) {
  return user.pictureUrl ? <img className={`avatar ${compact ? "avatar-compact" : ""}`} src={user.pictureUrl} alt="" referrerPolicy="no-referrer" /> : <span className={`avatar avatar-fallback ${compact ? "avatar-compact" : ""}`}>{initials(user.name)}</span>;
}

function GoogleMark() {
  return <svg width="19" height="19" viewBox="0 0 18 18" aria-hidden="true"><path fill="#4285F4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.797 2.715v2.258h2.909c1.703-1.568 2.684-3.878 2.684-6.614Z"/><path fill="#34A853" d="M9 18c2.43 0 4.468-.806 5.956-2.181l-2.91-2.258c-.806.54-1.835.859-3.046.859-2.344 0-4.328-1.585-5.037-3.714H.955v2.332A9 9 0 0 0 9 18Z"/><path fill="#FBBC05" d="M3.963 10.706A5.41 5.41 0 0 1 3.682 9c0-.592.102-1.167.281-1.706V4.962H.955A9 9 0 0 0 0 9c0 1.452.347 2.827.955 4.038l3.008-2.332Z"/><path fill="#EA4335" d="M9 3.58c1.322 0 2.508.454 3.441 1.346l2.581-2.581C13.464.892 11.426 0 9 0A9 9 0 0 0 .955 4.962l3.008 2.332C4.672 5.165 6.656 3.58 9 3.58Z"/></svg>;
}

function LoadingScreen() {
  return <div className="loading-screen"><Brand /><LoaderCircle className="spin" /><span>Je veilige omgeving laden…</span></div>;
}

type FileActionsProps = { file: DriveFile; onPreview: () => void; onStar: () => void; onDelete: () => void };

async function api<T = unknown>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: "same-origin", ...init });
  const type = response.headers.get("Content-Type") ?? "";
  const body = type.includes("application/json") ? await response.json() as T & ApiError : null;
  if (!response.ok) throw new Error(body?.error || `Aanvraag mislukt (${response.status}).`);
  return body as T;
}

function contentUrl(file: DriveFile, download = false) {
  return `/api/files/${encodeURIComponent(file.id)}/content${download ? "?download=1" : ""}`;
}

function messageFrom(reason: unknown) { return reason instanceof Error ? reason.message : "Er ging iets mis."; }
function initials(name: string) { return name.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "FS"; }
function firstName(name: string) { return name.trim().split(/\s+/)[0] || name; }
function extension(name: string) { const value = name.includes(".") ? name.split(".").pop() ?? "FILE" : "FILE"; return value.slice(0, 5).toUpperCase(); }
function formatBytes(bytes: number) { if (!bytes) return "0 B"; const units = ["B", "KB", "MB", "GB", "TB"]; const power = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1); return `${(bytes / 1024 ** power).toLocaleString("nl-NL", { maximumFractionDigits: power ? 1 : 0 })} ${units[power]}`; }
function formatDate(value: string) { return new Intl.DateTimeFormat("nl-NL", { day: "numeric", month: "short", year: new Date(value).getFullYear() === new Date().getFullYear() ? undefined : "numeric" }).format(new Date(value)); }
function greeting() { const hour = new Date().getHours(); return hour < 12 ? "Goedemorgen" : hour < 18 ? "Goedemiddag" : "Goedenavond"; }
function sectionTitle(section: Section) { return section === "recent" ? "Recent geopend" : section === "starred" ? "Met ster" : "Mijn bestanden"; }
function sectionSubtitle(section: Section, count: number) { return section === "recent" ? "Bestanden van de afgelopen 30 dagen." : section === "starred" ? "Je belangrijkste bestanden bij elkaar." : `${count} ${count === 1 ? "bestand staat" : "bestanden staan"} veilig voor je klaar.`; }
function friendlyType(file: DriveFile) { if (file.type.startsWith("image/")) return "Afbeelding"; if (file.type === "application/pdf") return "PDF"; if (file.type.startsWith("video/")) return "Video"; if (file.type.startsWith("audio/")) return "Audio"; if (file.type.includes("spreadsheet") || /\.(xlsx?|csv)$/i.test(file.name)) return "Spreadsheet"; if (file.type.includes("word") || /\.(docx?|odt)$/i.test(file.name)) return "Document"; if (file.type.includes("zip") || /\.(zip|rar|7z|tar|gz)$/i.test(file.name)) return "Archief"; return extension(file.name); }
function fileColor(file: DriveFile) { if (file.type.startsWith("image/")) return "type-image"; if (file.type === "application/pdf") return "type-pdf"; if (file.type.startsWith("video/")) return "type-video"; if (file.type.startsWith("audio/")) return "type-audio"; if (file.type.includes("spreadsheet") || /\.(xlsx?|csv)$/i.test(file.name)) return "type-sheet"; if (file.type.includes("zip") || /\.(zip|rar|7z|tar|gz)$/i.test(file.name)) return "type-archive"; if (file.type.includes("word") || file.type.startsWith("text/")) return "type-doc"; return "type-generic"; }
function fileIcon(file: DriveFile, size: number) { if (file.type.startsWith("image/")) return <FileImage size={size} />; if (file.type === "application/pdf" || file.type.includes("word") || file.type.startsWith("text/")) return <FileText size={size} />; if (file.type.startsWith("video/")) return <FileVideo size={size} />; if (file.type.startsWith("audio/")) return <FileAudio size={size} />; if (file.type.includes("spreadsheet") || /\.(xlsx?|csv)$/i.test(file.name)) return <FileSpreadsheet size={size} />; if (file.type.includes("zip") || /\.(zip|rar|7z|tar|gz)$/i.test(file.name)) return <Archive size={size} />; return <File size={size} />; }
